import { WEATHER_TILE_CHANNELS } from "@nova/constants"
import { Layer, project32 } from "@deck.gl/core"
import type { LayerContext, LayerProps } from "@deck.gl/core"
import type { Texture } from "@luma.gl/core"
import { Geometry, Model } from "@luma.gl/engine"
import { ColorStop, buildColormapGlsl } from "../shared/colormap"
import { FrameImage, FrameTextureCache } from "../shared/frameTextureCache"
import { HEATMAP_QUAD_POSITIONS, HEATMAP_QUAD_VERTEX_COUNT } from "../shared/heatmapGeometry"

export const RADAR_DBZ_COLOR_STOPS: ReadonlyArray<ColorStop> = [
    { value: -10, color: [0.11, 0.08, 0.39] },
    { value: 0, color: [0.2, 0.25, 0.7] },
    { value: 10, color: [0.15, 0.45, 0.8] },
    { value: 20, color: [0.26, 0.7, 0.62] },
    { value: 30, color: [0.5, 0.78, 0.3] },
    { value: 40, color: [0.93, 0.82, 0.25] },
    { value: 50, color: [0.87, 0.3, 0.25] },
    { value: 60, color: [0.75, 0.22, 0.58] },
    { value: 65, color: [0.62, 0.2, 0.6] },
]

export const RADAR_FADE_IN_MIN_DBZ = -5
export const RADAR_FADE_IN_MAX_DBZ = 5

const ALPHA_FLOOR = 0.45
const ALPHA_FULL_DBZ = 40

const BLUR_RADIUS = 2
const BLUR_SIGMA_MAX = 0.9
const BLUR_SIGMA_MIN = 0.45
const BLUR_TARGET_SCREEN_PX = 1.8

export const RADAR_HEATMAP_MAX_DBZ = RADAR_DBZ_COLOR_STOPS[RADAR_DBZ_COLOR_STOPS.length - 1].value

const { min: RADAR_DBZ_MIN, max: RADAR_DBZ_MAX } = WEATHER_TILE_CHANNELS.radar.reflectivityDbz
const RADAR_DBZ_SPAN = RADAR_DBZ_MAX - RADAR_DBZ_MIN

const HEATMAP_VS = `#version 300 es
in vec2 positions;
out vec2 v_normPos;

uniform vec4 u_bounds;

void main() {
    v_normPos = positions * 0.5 + 0.5;
    float lon = mix(u_bounds.x, u_bounds.z, v_normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, v_normPos.y);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
}
`

const HEATMAP_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 v_normPos;

uniform sampler2D u_reflTexture;
uniform float u_fadeInMinDbz;
uniform float u_fadeInMaxDbz;
uniform float u_opacity;
uniform float u_dbzMin;
uniform float u_dbzSpan;
uniform float u_blurSigma;

const float ALPHA_FLOOR = ${ALPHA_FLOOR.toFixed(2)};
const float ALPHA_FULL_DBZ = ${ALPHA_FULL_DBZ.toFixed(1)};
const int BLUR_RADIUS = ${BLUR_RADIUS};

out vec4 fragColor;

${buildColormapGlsl(RADAR_DBZ_COLOR_STOPS, "radarColormap")}

// dBZ decodes from R; alpha carries value-presence coverage. The blur runs on per-tap presence, not
// on dBZ: a dry cell is a present value of RADAR_DBZ_MIN, so averaging dBZ pits -30 against a 7 dBZ
// echo and the fade threshold lands wherever ~2/3 of the kernel happens to be wet — a hard edge moved
// inward, still locked to the texel grid. Thresholding first and averaging after gives a feather whose
// width is the kernel's, independent of how far below the ramp the dry fill sits.
//
// dBZ is then the presence-weighted mean, i.e. over wet taps only, so dry neighbours can't drag an
// isolated echo's colour down and small cores keep their peak.
//
// Taps land on texel centres and the sub-texel phase rides in the kernel weight, making this a
// reconstruction filter rather than a sliding box of bilinear reads. Offsetting from the fragment
// instead would sample every tap at the fragment's phase, and thresholding those interpolated reads
// modulates the result at texel frequency — a checker across the whole raster.
//
// The kernel is sized in texels, so its on-screen width tracks magnification: a sigma that
// reconstructs cleanly at two screen pixels per texel smears over sixteen once a texel covers that
// much. The caller scales it to hold the feather at a fixed screen width instead.
//
// Alpha is that presence (fade-in × coverage, so sampling across a coverage edge fades toward
// transparent instead of ringing a false gradient) times a value weight, which keeps drizzle a wash
// while cores reach the full requested opacity.
//
// Frames are shown as-is with no cross-frame blend: radar echoes are small and fast enough that a
// mix between two frames reads as two ghosted storms rather than one moving one.
void main() {
    vec2 size = vec2(textureSize(u_reflTexture, 0));
    vec2 coord = vec2(v_normPos.x, 1.0 - v_normPos.y) * size - 0.5;
    vec2 base = floor(coord);
    vec2 phase = coord - base;

    float twoSigmaSq = 2.0 * u_blurSigma * u_blurSigma;
    float presenceSum = 0.0;
    float valueSum = 0.0;
    float kernelSum = 0.0;
    for (int dy = -BLUR_RADIUS; dy <= BLUR_RADIUS + 1; dy++) {
        for (int dx = -BLUR_RADIUS; dx <= BLUR_RADIUS + 1; dx++) {
            vec2 offset = vec2(float(dx), float(dy));
            vec2 d = offset - phase;
            float k = exp(-dot(d, d) / twoSigmaSq);
            vec4 t = texture(u_reflTexture, (base + offset + 0.5) / size);
            float dbzTap = t.r * u_dbzSpan + u_dbzMin;
            float p = smoothstep(u_fadeInMinDbz, u_fadeInMaxDbz, dbzTap) * t.a;
            presenceSum += k * p;
            valueSum += k * p * dbzTap;
            kernelSum += k;
        }
    }

    if (presenceSum <= 0.0) discard;

    float presence = presenceSum / kernelSum;
    float dbz = valueSum / presenceSum;

    float alphaWeight = mix(ALPHA_FLOOR, 1.0, smoothstep(u_fadeInMaxDbz, ALPHA_FULL_DBZ, dbz));
    fragColor = vec4(radarColormap(dbz), u_opacity * presence * alphaWeight);
}
`

export type RadarHeatmapLayerProps = LayerProps & {
    image: FrameImage
    bounds: [number, number, number, number]
    fadeInMinDbz?: number
    fadeInMaxDbz?: number
    opacity?: number
}

const DEFAULT_OPACITY = 0.55

export class RadarHeatmapLayer extends Layer<RadarHeatmapLayerProps> {
    static layerName = "RadarHeatmapLayer"
    static defaultProps = {
        bounds: { type: "array", value: [-180, -90, 180, 90], compare: true },
        fadeInMinDbz: { type: "number", value: RADAR_FADE_IN_MIN_DBZ },
        fadeInMaxDbz: { type: "number", value: RADAR_FADE_IN_MAX_DBZ },
        opacity: { type: "number", value: DEFAULT_OPACITY, min: 0, max: 1 },
    }

    declare state: {
        textureCache: FrameTextureCache
        model: Model | null
    }

    getModels() {
        return this.state?.model ? [this.state.model] : []
    }

    initializeState(context: LayerContext): void {
        const { device } = context

        const shaders = this.getShaders({
            vs: HEATMAP_VS,
            fs: HEATMAP_FS,
            modules: [project32],
        })
        const model = new Model(device, {
            id: "radar-heatmap",
            ...shaders,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: {
                    positions: { size: 2, value: HEATMAP_QUAD_POSITIONS },
                },
            }),
            vertexCount: HEATMAP_QUAD_VERTEX_COUNT,
            parameters: {
                blendColorSrcFactor: "src-alpha",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        this.setState({ textureCache: new FrameTextureCache(), model })
    }

    finalizeState(context: LayerContext): void {
        this.state.textureCache?.destroy()
        this.state.model?.destroy()
        super.finalizeState(context)
    }

    draw(): void {
        const { model, textureCache } = this.state
        const { image } = this.props
        if (!model || !image || image.width === 0 || image.height === 0) return

        const create = (image: FrameImage): Texture =>
            this.context.device.createTexture({
                width: image.width,
                height: image.height,
                format: "rgba8unorm",
                data: image.bitmap,
                sampler: { magFilter: "linear", minFilter: "linear" },
            })

        const [west, south, east, north] = this.props.bounds
        const lat = (south + north) / 2
        const { viewport } = this.context
        const texelScreenPx =
            Math.abs(viewport.project([east, lat])[0] - viewport.project([west, lat])[0]) /
            image.width
        const blurSigma = Math.min(
            BLUR_SIGMA_MAX,
            Math.max(BLUR_SIGMA_MIN, BLUR_TARGET_SCREEN_PX / texelScreenPx)
        )

        model.setBindings({ u_reflTexture: textureCache.get(image, create) })
        model.setUniforms({
            u_bounds: this.props.bounds,
            u_blurSigma: blurSigma,
            u_fadeInMinDbz: this.props.fadeInMinDbz ?? RADAR_FADE_IN_MIN_DBZ,
            u_fadeInMaxDbz: this.props.fadeInMaxDbz ?? RADAR_FADE_IN_MAX_DBZ,
            u_opacity: this.props.opacity ?? DEFAULT_OPACITY,
            u_dbzMin: RADAR_DBZ_MIN,
            u_dbzSpan: RADAR_DBZ_SPAN,
        })
        model.draw(this.context.renderPass)
    }
}
